import { Object as FabricObject } from 'fabric/fabric-impl';
import type { FabricEditor } from 'modules/fabric-editor';
import { saveTemplate, selectActiveTemplate, selectIsSaving } from 'modules/manager/store';
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { useEditorDirty } from './use-editor-dirty';

export interface UseSaveTemplateResult {
  save: () => Promise<void>;
  isDirty: boolean;
  isSaving: boolean;
  canSave: boolean;
}

export function useSaveTemplate(editor: FabricEditor | null): UseSaveTemplateResult {
  const dispatch = useAppDispatch();
  const template = useAppSelector(selectActiveTemplate);
  const isSaving = useAppSelector(selectIsSaving);
  const isDirty = useEditorDirty(editor);

  const save = useCallback(async () => {
    if (!editor || !template) return;
    if (!editor.dirty?.isDirty()) return;
    if (isSaving) return;

    const json = editor.canvas.toJSON(['data']) as { version: string; objects: FabricObject[] };
    const result = await dispatch(
      saveTemplate({ id: template.id, objects: json.objects ?? [], version: json.version }),
    );
    if (saveTemplate.fulfilled.match(result)) {
      editor.dirty?.markClean();
    }
  }, [editor, template, isSaving, dispatch]);

  return {
    save,
    isDirty,
    isSaving,
    canSave: isDirty && !isSaving && editor !== null && template !== null,
  };
}
